import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SosIcon from "@mui/icons-material/Sos";
import CloseIcon from "@material-ui/icons/Close";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";

import { API_URL } from "../../utils/setApiUrl";

const useStyles = makeStyles(() => ({
  "@keyframes flashColor": {
    "0%, 100%": { backgroundColor: "#bb2124" },
    "50%": { backgroundColor: "#e57373" },
  },
  "@keyframes flashIcon": {
    "0%, 100%": { opacity: 1 },
    "50%": { opacity: 0.5 },
  },
  flashingCard: {
    animation: "$flashColor 1s infinite",
    backgroundColor: "#bb2124",
  },
  flashingIcon: {
    animation: "$flashIcon 0.5s infinite",
  },
  sosZone: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 2000,
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
  },
  inactiveCard: {
    minWidth: 280,
    maxWidth: 400,
    borderRadius: "8px",
    textAlign: "center",
    backgroundColor: "#f5f5f5",
    border: "1px solid #e0e0e0",
  },
  roomCard: {
    minWidth: 200,
    maxWidth: 300,
    color: "#ffffff",
    borderRadius: "8px",
    textAlign: "center",
  },
  dismissBtn: {
    backgroundColor: "#ffffff",
    color: "#bb2124",
    fontWeight: "bold",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
}));

const SosLoginAlert = () => {
  const classes = useStyles();
  const [sosRooms, setSosRooms] = useState([]);
  const [sosEnabled, setSosEnabled] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const audioRef = useRef(null);
  const baseUrl = API_URL;

  useEffect(() => {
    const checkSos = async () => {
      try {
        const response = await axios.get(`${baseUrl}/sos/active`);
        const { sosEnabled: enabled, rooms } = response.data;

        setSosEnabled(enabled);
        setLoaded(true);

        if (!enabled || !rooms || rooms.length === 0) {
          setSosRooms([]);
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            audioRef.current = null;
          }
          return;
        }

        setSosRooms(rooms);

        // Play SOS sound (loop until dismissed)
        if (rooms.length > 0 && !audioRef.current) {
          const audio = new Audio("./sos.mp3");
          audio.loop = true;
          audio.play().catch((err) =>
            console.error("SOS audio playback failed:", err)
          );
          audioRef.current = audio;
        }
      } catch (err) {
        // Silently fail - don't disrupt login
      }
    };

    checkSos();
    const interval = setInterval(checkSos, 10000);

    return () => {
      clearInterval(interval);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
    };
  }, [baseUrl]);

  const handleDismiss = async (room) => {
    try {
      await axios.post(`${baseUrl}/sos/dismiss`, { mac: room.mac });

      const remaining = sosRooms.filter((r) => r.id !== room.id);
      setSosRooms(remaining);

      // Stop audio if no more active SOS
      if (remaining.length === 0 && audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
    } catch (err) {
      console.error("Failed to dismiss SOS:", err);
    }
  };

  // Don't render until first check completes, and hide if SOS system is disabled
  if (!loaded || !sosEnabled) return null;

  return (
    <Box className={classes.sosZone}>
      {sosRooms.length === 0 ? (
        <Card className={classes.inactiveCard}>
          <CardContent>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              mb={1}
            >
              <CheckCircleOutlineIcon
                style={{ color: "#4caf50", fontSize: 28, marginRight: 8 }}
              />
              <Typography
                variant="body1"
                style={{ color: "#666", fontWeight: 500 }}
              >
                Няма активен SOS сигнал по стаите
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        sosRooms.map((room) => (
          <Card
            key={room.id}
            className={`${classes.roomCard} ${classes.flashingCard}`}
          >
            <CardContent style={{ paddingBottom: "8px" }}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                mb={1}
              >
                <SosIcon
                  className={classes.flashingIcon}
                  style={{ color: "#ffffff", fontSize: 32, marginRight: 8 }}
                />
                <Typography
                  variant="h4"
                  style={{ color: "#ffffff", fontWeight: "bold" }}
                >
                  {room.name}
                </Typography>
              </Box>
              <Typography
                variant="body2"
                style={{ color: "#ffffff", marginBottom: 8 }}
              >
                SOS ACTIVE
              </Typography>
              <Button
                variant="contained"
                className={classes.dismissBtn}
                onClick={() => handleDismiss(room)}
                startIcon={<CloseIcon />}
                fullWidth
              >
                Stop SOS
              </Button>
            </CardContent>
          </Card>
        ))
      )}
    </Box>
  );
};

export default SosLoginAlert;
